import { useCallback, useEffect, useRef, useState } from "react";

import { LONG_PRESS_MOVE_TOLERANCE_PX, LONG_PRESS_MS } from "./grid.constants";

interface IUseLongPressArgs {
  onLongPress: () => void;
  /** Off once the gesture's job is done — in edit mode dnd-kit owns the pointer. */
  enabled: boolean;
}

/**
 * Press and hold to enter customize mode, the way a home screen does it.
 *
 * Three things make this more than a `setTimeout`:
 *
 * **Scrolling must cancel it.** The canvas scrolls, and a touch that begins as a
 * press and turns into a flick must not arm anything. Movement past a small
 * tolerance cancels, and so does the browser's own `pointercancel` when it takes
 * the gesture over for scrolling. `touch-action` is deliberately left alone —
 * locking it would trade a working scroll for a marginally crisper press.
 *
 * **The release must not click through.** A widget is full of links. Without
 * suppression, holding one to enter customize mode and letting go navigates away
 * the instant the mode opens. One capture-phase listener swallows the click that
 * the release would otherwise produce.
 *
 * **It must say something happened.** There is no visual until the timer fires,
 * so the fire is where the feedback belongs: a short vibrate where the platform
 * has one, and the caller's own jiggle everywhere else.
 */
export function useLongPress({ onLongPress, enabled }: IUseLongPressArgs) {
  const [isPressing, setIsPressing] = useState(false);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const originRef = useRef<{ x: number; y: number } | null>(null);

  const onLongPressRef = useRef(onLongPress);
  useEffect(() => {
    onLongPressRef.current = onLongPress;
  }, [onLongPress]);

  const cancel = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    originRef.current = null;
    setIsPressing(false);
  }, []);

  useEffect(() => cancel, [cancel]);

  // Nothing half-pressed survives the gesture being switched off.
  useEffect(() => {
    if (!enabled) cancel();
  }, [enabled, cancel]);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (!enabled) return;
      // Primary button / first touch only: a right-click is a different gesture.
      if (event.button !== 0 || !event.isPrimary) return;

      originRef.current = { x: event.clientX, y: event.clientY };
      setIsPressing(true);

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setIsPressing(false);

        // Swallow the click the upcoming release would fire, so letting go of a
        // widget you long-pressed does not follow the link under your finger.
        const swallow = (click: MouseEvent) => {
          click.preventDefault();
          click.stopPropagation();
        };
        document.addEventListener("click", swallow, {
          capture: true,
          once: true,
        });
        // If the finger lifts somewhere that produces no click at all, the
        // listener would sit armed and eat an unrelated click later.
        setTimeout(
          () => document.removeEventListener("click", swallow, true),
          500,
        );

        navigator.vibrate?.(12);
        onLongPressRef.current();
      }, LONG_PRESS_MS);
    },
    [enabled],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const origin = originRef.current;
      if (!origin) return;

      const travelled = Math.hypot(
        event.clientX - origin.x,
        event.clientY - origin.y,
      );
      if (travelled > LONG_PRESS_MOVE_TOLERANCE_PX) cancel();
    },
    [cancel],
  );

  return {
    /** True while a press is being timed — the caller shows it as a slight give. */
    isPressing,
    handlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: cancel,
      onPointerCancel: cancel,
      onPointerLeave: cancel,
      // A context menu popping mid-hold both interrupts the gesture and looks
      // like a bug, so it is suppressed for the duration of the press only.
      onContextMenu: (event: React.MouseEvent) => {
        if (originRef.current) event.preventDefault();
      },
    },
  };
}
